import { isArray, isObject } from '@websolutespa/bom-core';

export type IPaginationItem = any;

export type IPaginationOptions = {
  mode?: 'pagination' | 'infinite'; // default to pagination
  page?: number;
  perPage?: number;
  delayInMs?: number;
};

export type IPaginationInfo<T extends IPaginationItem = IPaginationItem> = {
  items: T[];
  hasMore: boolean;
  nextPage: number | undefined;
  page: number;
  pages: number;
  perPage: number;
  prevPage: number | undefined;
  total: number;
};

export function isPaginationInfo(item: unknown): item is IPaginationInfo {
  return isObject(item) && (
    typeof item.page === 'number' &&
    typeof item.pages === 'number' &&
    typeof item.perPage === 'number' &&
    typeof item.total === 'number' &&
    isArray(item.items)
  );
}

export function getPagedItems<T extends IPaginationItem = IPaginationItem>(
  items: T[],
  page: number,
  perPage: number
): T[] {
  const from = (page - 1) * perPage;
  const to = Math.min(page * perPage, items.length);
  return items.slice(from, to);
}

export function getMoreItems<T extends IPaginationItem = IPaginationItem>(
  items: T[],
  page: number,
  perPage: number
): T[] {
  const total = items.length;
  const position = Math.min(total, perPage * page);
  return items.slice(0, position);
}

export function getPaginationInfo<T extends IPaginationItem = IPaginationItem>(
  items: T[],
  options?: IPaginationOptions
): IPaginationInfo<T> {
  const mode = options?.mode || 'pagination';
  const page = options?.page || 1;
  const perPage = options?.perPage || 12;
  const total = items.length;
  const pages = Math.ceil(total / perPage);
  const hasMore = page < pages;
  const prevPage = page > 1 ? page - 1 : undefined;
  const nextPage = page < pages ? page + 1 : undefined;
  const pagedItems = mode === 'pagination' ?
    getPagedItems(items, page, perPage) :
    getMoreItems(items, page, perPage);
  return {
    items: pagedItems,
    hasMore,
    nextPage,
    page,
    pages,
    perPage,
    prevPage,
    total,
  };
}
